// packages/web/src/components/AdminStock.tsx
import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Layers,
  AlertTriangle,
  Search,
  Plus,
  Minus,
  History,
  X,
  Package,
} from 'lucide-react'
import { apiGetWithMeta } from '../lib/api'
import { useStoreCategories, getCategoryLabel } from '../lib/storeCategories'
import { DataTablePagination } from './DataTablePagination'
import { AppDataTable } from './AppDataTable'
import { SearchableSelect } from './SearchableSelect'
import type { Product } from '@buddys-choice/shared'

interface StockItem {
  stock_kind?: 'product' | 'variant' | 'combo'
  variant_id: string | null
  product_id: string | null
  combo_id?: string | null
  sku: string
  stock_qty: number
  low_stock_threshold: number
  pending_order_qty: number
  price_override: number | null
  product_name: string
  base_price: number
  category: string
  variant_description: string
  is_low_stock: number
}

async function fetchAllProducts(): Promise<Product[]> {
  const pageSize = 100
  const first = await apiGetWithMeta<Product[]>('products', {
    page: 1,
    pageSize,
    sortBy: 'name',
    sortDir: 'asc',
  })
  const rows = [...(first.data || [])]
  const totalPages = first.meta?.totalPages ?? 1
  for (let nextPage = 2; nextPage <= totalPages; nextPage++) {
    const next = await apiGetWithMeta<Product[]>('products', {
      page: nextPage,
      pageSize,
      sortBy: 'name',
      sortDir: 'asc',
    })
    rows.push(...(next.data || []))
  }
  return rows
}

function shortfallQty(item: StockItem): number {
  return Math.max(0, item.pending_order_qty - item.stock_qty)
}

interface StockLog {
  id: string
  sku: string
  product_name: string
  adjustment_type: string
  quantity_change: number
  quantity_before: number
  quantity_after: number
  reason: string
  created_at: string
}

export const AdminStock: React.FC = () => {
  const navigate = useNavigate()
  const { categories } = useStoreCategories()
  const [items, setItems] = useState<StockItem[]>([])
  const [logs, setLogs] = useState<StockLog[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [logSearch, setLogSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [productFilter, setProductFilter] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [lowStockOnly, setLowStockOnly] = useState(false)
  const [activeTab, setActiveTab] = useState<'inventory' | 'history'>('inventory')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [logsPage, setLogsPage] = useState(1)
  const [logsTotalPages, setLogsTotalPages] = useState(1)
  const [logsTotal, setLogsTotal] = useState(0)
  const pageSize = 15

  useEffect(() => {
    if (activeTab !== 'inventory') return
    fetchAllProducts()
      .then(setProducts)
      .catch((err) => {
        console.error('Fetch products error:', err)
        setProducts([])
      })
  }, [activeTab])

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'All categories' },
      ...categories.map((cat) => ({
        value: cat.slug,
        label: cat.navLabel || cat.label,
      })),
    ],
    [categories],
  )

  const productOptions = useMemo(
    () => [
      { value: '', label: 'All products' },
      ...products.map((p) => ({
        value: p.id,
        label: p.name,
        keywords: p.sku,
      })),
    ],
    [products],
  )

  useEffect(() => {
    if (activeTab === 'inventory') {
      fetchStock()
    } else {
      fetchLogs()
    }
  }, [search, categoryFilter, productFilter, lowStockOnly, activeTab, page, logsPage, logSearch])

  const fetchStock = () => {
    setLoading(true)
    apiGetWithMeta<StockItem[]>('stock', {
      search: search || undefined,
      category: categoryFilter || undefined,
      productId: productFilter || undefined,
      lowStockOnly: lowStockOnly || undefined,
      page,
      pageSize,
    })
      .then((res) => {
        setItems(res.data || [])
        setTotal(res.meta?.total ?? 0)
        setTotalPages(res.meta?.totalPages ?? 1)
      })
      .catch((err) => console.error('Fetch stock error:', err))
      .finally(() => setLoading(false))
  }

  const fetchLogs = () => {
    setLoading(true)
    apiGetWithMeta<StockLog[]>('stock/logs', {
      page: logsPage,
      pageSize,
      search: logSearch || undefined,
    })
      .then((res) => {
        setLogs(res.data || [])
        setLogsTotal(res.meta?.total ?? 0)
        setLogsTotalPages(res.meta?.totalPages ?? 1)
      })
      .catch((err) => console.error('Fetch logs error:', err))
      .finally(() => setLoading(false))
  }

  const handleOpenAdjust = (item: StockItem, isIncrement: boolean) => {
    const targetId = item.variant_id || item.product_id || item.combo_id
    if (!targetId) return
    navigate({
      to: '/admin/stock/$variantId/adjust',
      params: { variantId: targetId },
      search: { change: isIncrement ? 5 : -1 },
    })
  }

  const clearInventoryFilters = () => {
    setSearch('')
    setCategoryFilter('')
    setProductFilter('')
    setLowStockOnly(false)
    setPage(1)
  }

  const clearLogFilters = () => {
    setLogSearch('')
    setLogsPage(1)
  }

  const hasInventoryFilters = Boolean(search || categoryFilter || productFilter || lowStockOnly)
  const hasLogFilters = Boolean(logSearch)

  const inventoryColumns = useMemo(
    () => [
      {
        name: 'SKU',
        sortable: true,
        sortValue: (item: StockItem) => item.sku,
        selector: (item: StockItem) => (
          <span className="font-mono text-[var(--gold-main)] font-semibold">{item.sku}</span>
        ),
      },
      {
        name: 'Product',
        cell: (item: StockItem) => (
          <div>
            <div className="font-medium">{item.product_name}</div>
            <div className="text-[10px] text-[var(--text-muted)]">{item.variant_description || 'Standard'}</div>
          </div>
        ),
      },
      {
        name: 'Category',
        selector: (item: StockItem) => getCategoryLabel(item.category, categories),
      },
      {
        name: 'On Hand',
        sortable: true,
        sortValue: (item: StockItem) => item.stock_qty,
        cell: (item: StockItem) => (
          <span className={`font-mono font-bold ${item.is_low_stock ? 'text-amber-500' : ''}`}>
            {item.stock_qty}
          </span>
        ),
      },
      {
        name: 'Open Orders',
        selector: (item: StockItem) => (
          <span className="font-mono font-bold text-[var(--gold-main)]">{item.pending_order_qty ?? 0}</span>
        ),
      },
      {
        name: 'Need to Make',
        cell: (item: StockItem) => {
          const shortfall = shortfallQty(item)
          return shortfall > 0 ? (
            <span className="font-mono font-bold text-amber-500">{shortfall}</span>
          ) : (
            <span className="text-[var(--text-faint)]">—</span>
          )
        },
      },
      {
        name: 'Status',
        cell: (item: StockItem) => {
          const shortfall = shortfallQty(item)
          if (shortfall > 0) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-amber-500/15 text-amber-500 border border-amber-500/30">
                <AlertTriangle className="w-3 h-3" /> Restock needed
              </span>
            )
          }
          if (item.is_low_stock) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-amber-500/10 text-amber-500 border border-amber-500/25">
                <AlertTriangle className="w-3 h-3" /> Low stock
              </span>
            )
          }
          return (
            <span className="inline-flex px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-emerald-500/10 text-emerald-500 border border-emerald-500/25">
              In stock
            </span>
          )
        },
      },
      {
        name: 'Actions',
        right: true,
        cell: (item: StockItem) => {
          const targetId = item.variant_id || item.product_id || item.combo_id
          return (
            <div className="flex items-center justify-end gap-2">
              {targetId ? (
                <>
                  <button type="button" onClick={() => handleOpenAdjust(item, true)} className="admin-action-chip admin-action-chip--success">
                    <Plus className="w-3.5 h-3.5" /> Restock
                  </button>
                  <button type="button" onClick={() => handleOpenAdjust(item, false)} className="admin-action-chip admin-action-chip--danger">
                    <Minus className="w-3.5 h-3.5" /> Deduct
                  </button>
                </>
              ) : null}
            </div>
          )
        },
      },
    ],
    [categories],
  )

  const logColumns = useMemo(
    () => [
      {
        name: 'Date & Time',
        sortable: true,
        sortValue: (log: StockLog) => log.created_at,
        selector: (log: StockLog) => (
          <span className="font-mono text-[var(--text-muted)]">{new Date(log.created_at).toLocaleString('en-IN')}</span>
        ),
      },
      {
        name: 'SKU',
        selector: (log: StockLog) => (
          <span className="font-mono text-[var(--gold-main)] font-semibold">{log.sku}</span>
        ),
      },
      {
        name: 'Product',
        selector: (log: StockLog) => log.product_name,
      },
      {
        name: 'Change',
        cell: (log: StockLog) => (
          <span className={`font-mono font-bold ${log.quantity_change >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
            {log.quantity_change >= 0 ? `+${log.quantity_change}` : log.quantity_change}
          </span>
        ),
      },
      {
        name: 'Before → After',
        selector: (log: StockLog) => (
          <span className="font-mono text-[var(--text-muted)]">{log.quantity_before} → {log.quantity_after}</span>
        ),
      },
      {
        name: 'Reason',
        selector: (log: StockLog) => <span className="italic">{log.reason}</span>,
      },
    ],
    [],
  )

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] flex items-center gap-2">
            <Layers className="w-6 h-6 text-[var(--gold-main)] shrink-0" />
            Stock & Inventory Control
          </h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            On-hand stock, open pre-orders, and restock when orders exceed inventory.
            {activeTab === 'inventory' && total > 0 && (
              <span className="text-[var(--gold-main)] ml-1">({total} variants)</span>
            )}
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex bg-[var(--bg-surface-2)] p-1 rounded-xl border border-[var(--border)] text-xs shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('inventory')}
            className={`px-4 py-2 rounded-lg font-semibold transition flex items-center gap-1.5 ${
              activeTab === 'inventory'
                ? 'bg-[var(--gold-main)] text-[var(--gold-contrast)] shadow-sm'
                : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            <Package className="w-3.5 h-3.5" />
            Current Inventory
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg font-semibold transition flex items-center gap-1.5 ${
              activeTab === 'history'
                ? 'bg-[var(--gold-main)] text-[var(--gold-contrast)] shadow-sm'
                : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            <History className="w-3.5 h-3.5" />
            Audit Logs
          </button>
        </div>
      </div>

      {activeTab === 'inventory' ? (
        <>
          {/* Filter Toolbar */}
          <div className="glass-luxury-card p-3 sm:p-4 overflow-visible">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative shrink-0 w-[min(100%,240px)] sm:w-56">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                  placeholder="Product name or SKU..."
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-2.5 pl-9 pr-3 text-xs text-[var(--text-primary)] outline-none"
                />
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--gold-main)] pointer-events-none" />
              </div>

              <SearchableSelect
                value={productFilter}
                onChange={(v) => { setProductFilter(v); setPage(1) }}
                options={productOptions}
                placeholder="All products"
                aria-label="Filter by product"
                className="shrink-0 w-40 sm:w-52"
              />

              <SearchableSelect
                value={categoryFilter}
                onChange={(v) => { setCategoryFilter(v); setPage(1) }}
                options={categoryOptions}
                placeholder="All categories"
                aria-label="Filter by category"
                className="shrink-0 w-36 sm:w-44"
              />

              <button
                type="button"
                onClick={() => { setLowStockOnly((v) => !v); setPage(1) }}
                aria-pressed={lowStockOnly}
                className={`shrink-0 flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition whitespace-nowrap ${
                  lowStockOnly
                    ? 'bg-amber-500/15 border-amber-500/40 text-amber-500'
                    : 'bg-[var(--bg-main)] border-[var(--border)] text-[var(--text-muted)] hover:border-amber-500/30 hover:text-amber-500'
                }`}
              >
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                Low stock only
              </button>

              {hasInventoryFilters && (
                <button
                  type="button"
                  onClick={clearInventoryFilters}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-[var(--border)] text-xs text-[var(--text-muted)] hover:text-red-400 hover:border-red-500/30 transition whitespace-nowrap"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Inventory — desktop table */}
          <div className="glass-luxury-card overflow-hidden hidden md:block">
            <AppDataTable
              columns={inventoryColumns}
              data={items}
              keyField={(item) => item.variant_id ?? item.product_id ?? item.combo_id ?? item.sku}
              progressPending={loading}
              noDataComponent="No stock items match your filters."
            />
          </div>

          {/* Inventory — mobile cards */}
          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="admin-mobile-card text-center py-8">
                <span className="spinner text-[var(--gold-dark)]" />
              </div>
            ) : items.length === 0 ? (
              <div className="admin-mobile-card text-center py-8 text-xs text-[var(--text-primary)]/40">
                No stock items found.
              </div>
            ) : (
              items.map((item) => {
                const shortfall = shortfallQty(item)
                const needsWork = shortfall > 0
                return (
                <div key={item.variant_id ?? item.product_id} className="glass-luxury-card p-4 space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="font-mono text-xs font-bold text-[var(--gold-main)]">{item.sku}</p>
                      <p className="text-sm font-medium text-[var(--text-primary)] mt-0.5 line-clamp-2">{item.product_name}</p>
                      <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{item.variant_description || 'Standard'}</p>
                      <p className="text-[10px] text-[var(--text-faint)] mt-1">{getCategoryLabel(item.category, categories)}</p>
                    </div>
                    {needsWork ? (
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold uppercase bg-amber-500/15 text-amber-500 border border-amber-500/30">
                        Restock
                      </span>
                    ) : item.is_low_stock ? (
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold uppercase bg-amber-500/10 text-amber-500 border border-amber-500/25">
                        Low
                      </span>
                    ) : (
                      <span className="shrink-0 inline-flex px-2 py-1 rounded-full text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-500 border border-emerald-500/25">
                        OK
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div>
                      <p className="admin-mobile-card-label">On hand</p>
                      <p className="font-mono font-bold">{item.stock_qty}</p>
                    </div>
                    <div>
                      <p className="admin-mobile-card-label">Orders</p>
                      <p className="font-mono font-bold text-[var(--gold-dark)]">{item.pending_order_qty ?? 0}</p>
                    </div>
                    <div>
                      <p className="admin-mobile-card-label">Need</p>
                      <p className={`font-mono font-bold ${needsWork ? 'text-amber-400' : 'text-[var(--text-faint)]'}`}>
                        {needsWork ? shortfall : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {(item.variant_id || item.product_id || item.combo_id) ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOpenAdjust(item, true)}
                          className="admin-action-chip admin-action-chip--success flex-1 min-w-[120px] justify-center"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Restock
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenAdjust(item, false)}
                          className="admin-action-chip admin-action-chip--danger flex-1 min-w-[120px] justify-center"
                        >
                          <Minus className="w-3.5 h-3.5" />
                          Deduct stock
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                )
              })
            )}
          </div>

          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            className="pt-2"
          />
        </>
      ) : (
        /* Audit Logs Tab */
        <>
        <div className="glass-luxury-card p-3 sm:p-4">
          <div className="flex flex-nowrap items-center gap-2 overflow-x-auto">
            <div className="relative shrink-0 w-[min(100%,240px)] sm:w-56">
              <input
                type="text"
                value={logSearch}
                onChange={(e) => { setLogSearch(e.target.value); setLogsPage(1) }}
                placeholder="Search SKU or product..."
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-2.5 pl-9 pr-3 text-xs text-[var(--text-primary)] outline-none"
              />
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--gold-main)] pointer-events-none" />
            </div>
            {hasLogFilters && (
              <button
                type="button"
                onClick={clearLogFilters}
                className="shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-[var(--border)] text-xs text-[var(--text-muted)] hover:text-red-400 hover:border-red-500/30 transition"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Stock logs — desktop table */}
        <div className="glass-luxury-card overflow-hidden hidden md:block">
          <AppDataTable
            columns={logColumns}
            data={logs}
            keyField={(log) => log.id}
            progressPending={loading}
            noDataComponent="No stock adjustments match your search."
          />
        </div>

        {/* Audit logs — mobile cards */}
        <div className="md:hidden space-y-3">
          {loading ? (
            <div className="admin-mobile-card text-center py-8">
              <span className="spinner text-[var(--gold-dark)]" />
            </div>
          ) : logs.length === 0 ? (
            <div className="admin-mobile-card text-center py-8 text-xs text-[var(--text-primary)]/40">
              No stock adjustments recorded yet.
            </div>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="glass-luxury-card p-4 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-mono text-xs font-bold text-[var(--gold-dark)]">{log.sku}</p>
                    <p className="text-sm font-medium text-[var(--text-primary)] mt-0.5">{log.product_name}</p>
                  </div>
                  <span className={`font-mono font-bold text-sm ${log.quantity_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {log.quantity_change >= 0 ? `+${log.quantity_change}` : log.quantity_change}
                  </span>
                </div>
                <p className="text-[10px] text-[var(--text-muted)] font-mono mt-2">
                  {new Date(log.created_at).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  {log.quantity_before} → {log.quantity_after}
                </p>
                <p className="text-xs text-[var(--text-primary)]/80 italic mt-2 pt-2 border-t border-[var(--border)]">
                  {log.reason}
                </p>
              </div>
            ))
          )}
        </div>

        <DataTablePagination
          page={logsPage}
          totalPages={logsTotalPages}
          total={logsTotal}
          pageSize={pageSize}
          onPageChange={setLogsPage}
          className="pt-2"
        />
        </>
      )}

    </div>
  )
}
